import type {
  AssessmentInput,
  ConsolidationMode,
  Domain,
  DomainScore,
  Framework,
  MultiAssessmentInput,
  MultiRiskProfile,
  PerUseCaseProfile,
  Recommendation,
  RiskProfile,
  ThreatCoverageRow,
  ThreatExposure,
  UseCaseId,
} from "../shared/types";
import { profileLikelihood, profileImpact, profileLikelihoodWith, profileImpactWith, THREATS } from "../taxonomy";
import { visibleEnvOnly } from "../taxonomy/environment";
import {
  DEFAULT_RESIDUAL_FLOOR,
  DEFAULT_OVERALL_PEAK_WEIGHT,
} from "../taxonomy/weights";
import { clamp01, severity, toScore100, makeColorFns } from "../ui/utils";
import type { Threat } from "../shared/types";
import type { WeightsConfig, ColorConfig } from "../content/types";

const TARGET_LEVEL = 4;

// Primary controls fully count toward a threat's mitigation; secondary controls
// count half — consistent with how recommendations weight them (finding F5).
const PRIMARY_W = 1;
const SECONDARY_W = 0.5;

// V4.2 (FINDINGS.md F-02): an unanswered control means exactly ONE thing
// everywhere — "not in place", scored as level 1. Previously
// `weightedAvgMaturity` defaulted a blank to 1 while `domainScores` *excluded*
// it from the pillar mean, so the same blank answer simultaneously raised the
// risk score (counted as absent) and the maturity stage (an org that answered only
// its three strongest controls reported the mean of those three). Both paths now
// route through `levelOf`, and the report states the assumption alongside a
// completeness count so the user can see how many blanks fed it.
export const UNANSWERED_LEVEL = 1;

export function levelOf(
  maturity: Record<string, number>,
  controlId: string,
): number {
  return maturity[controlId] ?? UNANSWERED_LEVEL;
}

// How many of the framework's controls the user actually answered. Surfaced so
// "Stage 2" from four answers is visibly different from "Stage 2" from fifteen.
export function maturityCompleteness(
  framework: Framework,
  maturity: Record<string, number>,
): { answered: number; total: number } {
  const ids = framework.domains.flatMap((d) =>
    d.subDomains.flatMap((sd) => sd.controls.map((c) => c.id)),
  );
  return {
    answered: ids.filter((id) => typeof maturity[id] === "number").length,
    total: ids.length,
  };
}

// Weighted mean maturity of a threat's controls (primary 1.0, secondary 0.5).
function weightedAvgMaturity(t: Threat, maturity: Record<string, number>): number {
  let num = 0;
  let den = 0;
  for (const id of t.primaryControlIds) {
    num += levelOf(maturity, id) * PRIMARY_W;
    den += PRIMARY_W;
  }
  for (const id of t.secondaryControlIds) {
    num += levelOf(maturity, id) * SECONDARY_W;
    den += SECONDARY_W;
  }
  return den ? num / den : UNANSWERED_LEVEL;
}

// Pillar means for the maturity stage. Unanswered controls count as level 1 (F-02),
// so a domain always has a defined mean over its full control set.
function domainScoresFrom(
  framework: Framework,
  maturity: Record<string, number>,
): DomainScore[] {
  return framework.domains.map((d) => {
    const levels = d.subDomains.flatMap((sd) =>
      sd.controls.map((c) => levelOf(maturity, c.id)),
    );
    const current = levels.length
      ? levels.reduce((a, b) => a + b, 0) / levels.length
      : 0;
    return { domainId: d.id, current, target: TARGET_LEVEL };
  });
}

// Maturity factor with a residual-risk floor: a maxed-out control reduces but
// never fully eliminates exposure (finding F1). maturity 1 → 1.0, maturity 5 →
// residualFloor, smooth between.
export function maturityFactorFrom(avgMaturity: number, residualFloor: number): number {
  const linear = clamp01(1 - (avgMaturity - 1) / 4);
  return residualFloor + (1 - residualFloor) * linear;
}

// Relevance-weighted, peak-aware overall (finding F2 + FAIR): weight each threat's
// risk by its loss magnitude (impact), so high-consequence threats dominate the
// portfolio and use cases with different threat counts stay comparable, then blend
// with the single worst threat so a lone critical isn't buried.
function aggregateOverall(
  exposures: ThreatExposure[],
  peakWeight: number,
): number {
  if (exposures.length === 0) return 0;
  let num = 0;
  let den = 0;
  let max = 0;
  for (const e of exposures) {
    const w = e.impact || 0;
    num += e.exposure * w;
    den += w;
    if (e.exposure > max) max = e.exposure;
  }
  const weightedMean = den ? num / den : 0;
  return clamp01((1 - peakWeight) * weightedMean + peakWeight * max);
}

// Scoring is data-injectable so runtime overrides (admin GUI) flow through the
// same math. A ScoringContext bundles the threat catalog, the FAIR likelihood /
// impact functions, and the severity classifier. Callers that don't pass one get
// the built-in defaults, so every existing call site is unchanged.
export type ScoringContext = {
  threats: Threat[];
  profileLikelihood: (
    useCase: UseCaseId,
    env: AssessmentInput["env"],
    threatId: string,
    autonomyLevel: number,
  ) => number;
  profileImpact: (env: AssessmentInput["env"], threatId: string) => number;
  severity: (exposure: number) => ThreatExposure["severity"];
  /**
   * V4.2 — raw 0..1 exposure → the 0–100 number the user is shown. It lives on
   * the context (rather than being imported directly) so recommendations are
   * ranked on the same calibration the admin's threshold overrides produce.
   */
  toScore100: (risk: number | null | undefined) => number;
  residualFloor: number;
  overallPeakWeight: number;
};

export const DEFAULT_SCORING_CONTEXT: ScoringContext = {
  threats: THREATS,
  profileLikelihood,
  profileImpact,
  severity,
  toScore100,
  residualFloor: DEFAULT_RESIDUAL_FLOOR,
  overallPeakWeight: DEFAULT_OVERALL_PEAK_WEIGHT,
};

// Build a context from a resolved content bundle (or its weights+colors+threats).
export function scoringContextFromBundle(bundle: {
  threats: Threat[];
  weights: WeightsConfig;
  colors: ColorConfig;
}): ScoringContext {
  const colorFns = makeColorFns(bundle.colors);
  return {
    threats: bundle.threats,
    profileLikelihood: (uc, env, threatId, autonomy) =>
      profileLikelihoodWith(bundle.weights, uc, env, threatId, autonomy),
    profileImpact: (env, threatId) =>
      profileImpactWith(bundle.weights, env, threatId),
    severity: colorFns.severity,
    toScore100: colorFns.toScore100,
    residualFloor: bundle.weights.residualFloor ?? DEFAULT_RESIDUAL_FLOOR,
    overallPeakWeight: bundle.weights.overallPeakWeight ?? DEFAULT_OVERALL_PEAK_WEIGHT,
  };
}

// V4.2 (FINDINGS.md F-07). Exposure of the same threat, same use case, same
// maturity, but with a NEUTRAL environment (no env answers, so every modifier
// is 1.0) and autonomy 3. Base weight, threat-impact prior and maturity factor
// are identical in both terms and cancel, so the ratio isolates the
// amplification this organisation's own configuration contributes.
const NEUTRAL_AUTONOMY = 3;

function elevationOf(
  ctx: ScoringContext,
  useCase: UseCaseId,
  env: AssessmentInput["env"],
  threatId: string,
  likelihoodRaw: number,
  impact: number,
): number {
  const baseL = ctx.profileLikelihood(useCase, {}, threatId, NEUTRAL_AUTONOMY);
  const baseI = ctx.profileImpact({}, threatId);
  const baseline = baseL * baseI;
  if (baseline <= 0) return 1;
  void env;
  return (likelihoodRaw * impact) / baseline;
}

// V4.2 (FINDINGS.md F-07). `topThreats` answers "what is your biggest risk"
// and stays ranked by absolute exposure — that is the question the score is
// built from, and an earlier attempt to interleave elevated threats directly
// into it was measured against the 50-case corpus and REVERTED: it improved
// threat *diversity* dramatically (no threat was left permanently unranked, and
// sensitive_disclosure's appearance rate fell from 41/50 to 33/50) but agreement
// with the held-out expert predictions fell 28/50 -> 17/50. The predictions ask
// a magnitude question, so the ranked list must answer a magnitude question.
//
// `distinctiveThreats` answers the other half of F-07 additively instead:
// threats this specific deployment elevates furthest above a neutral one, that
// the magnitude ranking does not already show. It takes no slot away from the
// ranked list, so it costs nothing against the corpus while still surfacing the
// model_extraction / model_dos class of threat that pure magnitude ranking
// buried in all 50 scenarios. `minElevation` of 1.25 means "at least a quarter
// more exposed than a typical deployment of this use case" — below that there
// is nothing distinctive to report, and the list is correctly empty.
const DISTINCTIVE_MIN_ELEVATION = 1.25;

export function distinctiveThreats(
  exposures: ThreatExposure[],
  alreadyShown: ThreatExposure[],
  limit = 3,
): ThreatExposure[] {
  const shown = new Set(alreadyShown.map((e) => e.threatId));
  return exposures
    .filter((e) => !shown.has(e.threatId) && (e.elevation ?? 1) >= DISTINCTIVE_MIN_ELEVATION)
    .sort(
      (a, b) =>
        (b.elevation ?? 1) - (a.elevation ?? 1) ||
        b.exposure - a.exposure ||
        a.threatId.localeCompare(b.threatId), // deterministic
    )
    .slice(0, limit);
}

export function computeRiskProfile(
  framework: Framework,
  input: AssessmentInput,
  ctx: ScoringContext = DEFAULT_SCORING_CONTEXT,
): RiskProfile {
  const useCase = input.useCase;
  const maturity = input.maturity;
  // F-11: answers to questions the user was never shown must not score.
  const env = useCase ? visibleEnvOnly(input.env, useCase) : input.env;
  const autonomyLevel = input.autonomyLevel ?? 3;
  const exposures: ThreatExposure[] = [];

  for (const t of ctx.threats) {
    const likelihoodRaw = useCase
      ? ctx.profileLikelihood(useCase, env, t.id, autonomyLevel)
      : 0;
    if (likelihoodRaw === 0) continue;
    const impact = ctx.profileImpact(env, t.id);
    const allCtrl = [...t.primaryControlIds, ...t.secondaryControlIds];
    const avgMaturity = weightedAvgMaturity(t, maturity);
    const maturityFactor = maturityFactorFrom(avgMaturity, ctx.residualFloor);
    // FAIR: maturity reduces LIKELIHOOD only; impact is the magnitude of loss and
    // is unchanged by process maturity. Risk = likelihood × impact.
    const likelihood = clamp01(likelihoodRaw * maturityFactor);
    const exposure = clamp01(likelihood * impact);
    exposures.push({
      threatId: t.id,
      exposure,
      likelihood,
      impact,
      baseWeight: likelihoodRaw, // pre-maturity likelihood, for recommendation recompute
      maturityFactor,
      contributingControlIds: allCtrl,
      severity: ctx.severity(exposure),
      elevation: useCase
        ? elevationOf(ctx, useCase, env, t.id, likelihoodRaw, impact)
        : 1,
    });
  }
  exposures.sort((a, b) => b.exposure - a.exposure);

  const domainScores = domainScoresFrom(framework, maturity);

  const overall = aggregateOverall(exposures, ctx.overallPeakWeight);

  const recommendations = buildRecommendations(framework, input.maturity, exposures, ctx);

  return {
    overall,
    overallLabel: ctx.severity(overall),
    topThreats: exposures.slice(0, 3),
    threatExposures: exposures,
    domainScores,
    recommendations,
    generatedAt: new Date().toISOString(),
  };
}

function buildRecommendations(
  framework: Framework,
  // V4.2 (FINDINGS.md F-18): maturity only. The consolidated caller used to pass
  // `{ env: {}, maturity }`, which read as "recommendations ignore the estate's
  // environment" — the exposures are the consolidated ones and already carry
  // every environment multiplier, so the empty env was inert, but nothing in the
  // signature said so. Narrowing the parameter makes the misreading impossible
  // and stops a future edit from reaching for the blank env by mistake.
  maturity: AssessmentInput["maturity"],
  exposures: ThreatExposure[],
  ctx: ScoringContext = DEFAULT_SCORING_CONTEXT,
): Recommendation[] {
  const controlIndex = indexControls(framework.domains);
  const threatById = new Map(ctx.threats.map((t) => [t.id, t]));
  const overallNow = aggregateOverall(exposures, ctx.overallPeakWeight);

  // Candidate controls = every control referenced by an exposed threat.
  const candidates = new Set<string>();
  for (const t of ctx.threats) {
    if (!exposures.some((e) => e.threatId === t.id)) continue;
    [...t.primaryControlIds, ...t.secondaryControlIds].forEach((c) => candidates.add(c));
  }

  const recs: Recommendation[] = [];
  for (const cid of candidates) {
    const ctrl = controlIndex.get(cid);
    if (!ctrl) continue;
    const current = maturity[cid] ?? 1;
    if (current >= TARGET_LEVEL) continue;
    // Actual marginal value (finding F6): recompute every exposure with this one
    // control raised to target, and measure the drop in the overall score. No more
    // saturating impact×gap heuristic.
    const hypo = { ...maturity, [cid]: TARGET_LEVEL };
    const newExposures = exposures.map((e) => {
      const t = threatById.get(e.threatId);
      if (!t) return e;
      const f = maturityFactorFrom(weightedAvgMaturity(t, hypo), ctx.residualFloor);
      const likelihood = clamp01(e.baseWeight * f);
      return { ...e, likelihood, exposure: clamp01(likelihood * e.impact) };
    });
    const expectedRiskReduction = clamp01(
      overallNow - aggregateOverall(newExposures, ctx.overallPeakWeight),
    );
    if (expectedRiskReduction <= 0) continue;

    recs.push({
      controlId: cid,
      controlName: ctrl.control.name,
      domainId: ctrl.domainId,
      current,
      target: TARGET_LEVEL,
      expectedRiskReduction,
      // V4.2: the delta the user actually reads. A difference cannot be pushed
      // through the (nonlinear) 0–100 calibration, so both endpoints go through
      // it and the *scores* are differenced.
      expectedScoreDrop: Math.max(
        0,
        ctx.toScore100(overallNow) -
          ctx.toScore100(aggregateOverall(newExposures, ctx.overallPeakWeight)),
      ),
      mappedStandards: ctrl.control.mappedStandards,
      rationale: `Improving from L${current} to L${TARGET_LEVEL} reduces exposure to ${countThreats(cid, ctx)} mapped threats.`,
    });
  }
  return rankRecommendations(recs);
}

/**
 * V4.2 (FINDINGS.md F-17). Three problems with `sort by expectedRiskReduction`:
 *
 *  1. It ignored cost. A control at L1 and a control at L3 both move to L4, but
 *     the first is three levels of work for its reduction and the second is one.
 *     Ranking by reduction *per level of effort* is what an assessor means by
 *     "do this first", and it is why broad, threat-heavy controls like
 *     `D1.SD2.C1` and `D3.SD2.C1` used to dominate top slots — they win on raw
 *     reduction alone regardless of how many maturity levels away they are.
 *  2. Ties fell through to `Set` iteration order, so reordering the threat
 *     catalog silently reordered the user's remediation plan. Ties now break on
 *     absolute reduction, then on controlId — total and stable.
 *  3. The top three could all sit in one pillar, which reads as "your whole
 *     problem is Protect" even when Govern is the weaker pillar. The first three
 *     slots are drawn from distinct domains where the candidates allow it; the
 *     rest of the list keeps the pure efficiency order.
 *
 * Ordering is by effort-adjusted value; the number *printed* is
 * `expectedScoreDrop`, so the list is not sorted by a hidden quantity — the
 * rationale copy states the effort adjustment explicitly.
 */
function rankRecommendations(recs: Recommendation[]): Recommendation[] {
  const effort = (r: Recommendation) => Math.max(1, r.target - r.current);
  const byValue = [...recs].sort(
    (a, b) =>
      b.expectedRiskReduction / effort(b) - a.expectedRiskReduction / effort(a) ||
      b.expectedRiskReduction - a.expectedRiskReduction ||
      a.controlId.localeCompare(b.controlId),
  );

  const head: Recommendation[] = [];
  const seenDomains = new Set<string>();
  for (const r of byValue) {
    if (head.length >= 3) break;
    if (seenDomains.has(r.domainId)) continue;
    head.push(r);
    seenDomains.add(r.domainId);
  }
  const taken = new Set(head.map((r) => r.controlId));
  return [...head, ...byValue.filter((r) => !taken.has(r.controlId))].slice(0, 10);
}

// Per (threat × domain) exposure cell. Returns null when threat has no
// mapped controls in that domain.
export function cellExposure(
  framework: Framework,
  input: AssessmentInput,
  threatId: string,
  domainId: string,
  ctx: ScoringContext = DEFAULT_SCORING_CONTEXT,
): number | null {
  const t = ctx.threats.find((x) => x.id === threatId);
  if (!t || !input.useCase) return null;
  const allCtrl = [...t.primaryControlIds, ...t.secondaryControlIds];
  const domain = framework.domains.find((d) => d.id === domainId);
  if (!domain) return null;
  const domainCtrlIds = new Set(
    domain.subDomains.flatMap((sd) => sd.controls.map((c) => c.id)),
  );
  const inDomain = allCtrl.filter((c) => domainCtrlIds.has(c));
  if (inDomain.length === 0) return null;
  const env = visibleEnvOnly(input.env, input.useCase); // F-11
  const likelihoodRaw = ctx.profileLikelihood(
    input.useCase,
    env,
    threatId,
    input.autonomyLevel ?? 3,
  );
  if (likelihoodRaw === 0) return null;
  const impact = ctx.profileImpact(env, threatId);
  // Weight primary vs secondary among this domain's mapped controls (F5).
  const primarySet = new Set(t.primaryControlIds);
  let num = 0;
  let den = 0;
  for (const id of inDomain) {
    const w = primarySet.has(id) ? PRIMARY_W : SECONDARY_W;
    num += (input.maturity[id] ?? 1) * w;
    den += w;
  }
  const avgMaturity = den ? num / den : 1;
  const maturityFactor = maturityFactorFrom(avgMaturity, ctx.residualFloor);
  return clamp01(clamp01(likelihoodRaw * maturityFactor) * impact);
}

function indexControls(domains: Domain[]) {
  const map = new Map<string, { domainId: string; control: Domain["subDomains"][number]["controls"][number] }>();
  for (const d of domains)
    for (const sd of d.subDomains)
      for (const c of sd.controls) map.set(c.id, { domainId: d.id, control: c });
  return map;
}

function countThreats(controlId: string, ctx: ScoringContext = DEFAULT_SCORING_CONTEXT): number {
  return ctx.threats.filter(
    (t) =>
      t.primaryControlIds.includes(controlId) ||
      t.secondaryControlIds.includes(controlId),
  ).length;
}

// ---------- V2: multi-use-case consolidation ----------

export function computeMultiRiskProfile(
  framework: Framework,
  input: MultiAssessmentInput,
  mode: ConsolidationMode = "max",
  ctx: ScoringContext = DEFAULT_SCORING_CONTEXT,
): MultiRiskProfile {
  const perUseCase: PerUseCaseProfile[] = input.useCases.map((u) => ({
    useCaseId: u.id,
    profile: computeRiskProfile(
      framework,
      {
        useCase: u.id,
        env: u.env,
        autonomyLevel: input.autonomyLevel,
        maturity: input.maturity,
      },
      ctx,
    ),
  }));

  const coverage: ThreatCoverageRow[] = ctx.threats.map((t) => {
    const row: ThreatCoverageRow = {
      threatId: t.id,
      perUseCase: {} as Record<UseCaseId, ThreatExposure | null>,
    };
    let anyHit = false;
    for (const p of perUseCase) {
      const e = p.profile.threatExposures.find((x) => x.threatId === t.id);
      row.perUseCase[p.useCaseId] = e ?? null;
      if (e) anyHit = true;
    }
    return anyHit ? row : null;
  }).filter((r): r is ThreatCoverageRow => r !== null);

  const consolidated = consolidateProfiles(framework, perUseCase, coverage, input, mode, ctx);

  return {
    consolidationMode: mode,
    perUseCase,
    consolidated,
    threatCoverage: coverage,
    generatedAt: new Date().toISOString(),
  };
}

function consolidateProfiles(
  framework: Framework,
  perUseCase: PerUseCaseProfile[],
  coverage: ThreatCoverageRow[],
  input: MultiAssessmentInput,
  mode: ConsolidationMode,
  ctx: ScoringContext = DEFAULT_SCORING_CONTEXT,
): RiskProfile {
  const exposures: ThreatExposure[] = coverage.map((row) => {
    const values: ThreatExposure[] = Object.values(row.perUseCase).filter(
      (v): v is ThreatExposure => v !== null,
    );
    if (values.length === 0) {
      return {
        threatId: row.threatId,
        exposure: 0,
        likelihood: 0,
        impact: 0,
        baseWeight: 0,
        maturityFactor: 0,
        contributingControlIds: [],
        severity: ctx.severity(0),
        elevation: 1,
      };
    }
    let exposure: number;
    let likelihood: number;
    let impact: number;
    let baseWeight: number;
    let maturityFactor: number;
    let elevation: number;
    if (mode === "max") {
      const top = values.reduce((a, b) => (b.exposure > a.exposure ? b : a));
      exposure = top.exposure;
      likelihood = top.likelihood;
      impact = top.impact;
      baseWeight = top.baseWeight;
      maturityFactor = top.maturityFactor;
      elevation = top.elevation ?? 1;
    } else {
      // V4.2 (FINDINGS.md F-03): average over the use cases the threat actually
      // applies to, not over the whole portfolio. Dividing by `perUseCase.length`
      // meant a threat relevant to 2 of 5 programmes was diluted 5x, and adding an
      // unrelated benign system *lowered* the estate's reported risk — the one
      // behaviour a risk tool must never have. `values` is already filtered to the
      // use cases with a non-null (non-zero base weight) exposure for this threat.
      const n = values.length;
      exposure = values.reduce((a, v) => a + v.exposure, 0) / n;
      likelihood = values.reduce((a, v) => a + v.likelihood, 0) / n;
      impact = values.reduce((a, v) => a + v.impact, 0) / n;
      baseWeight = values.reduce((a, v) => a + v.baseWeight, 0) / n;
      maturityFactor = values.reduce((a, v) => a + v.maturityFactor, 0) / n;
      elevation = values.reduce((a, v) => a + (v.elevation ?? 1), 0) / n;
    }
    return {
      threatId: row.threatId,
      exposure: clamp01(exposure),
      likelihood: clamp01(likelihood),
      impact: clamp01(impact),
      baseWeight,
      maturityFactor,
      contributingControlIds: values[0]?.contributingControlIds ?? [],
      severity: ctx.severity(clamp01(exposure)),
      elevation,
    };
  });
  exposures.sort((a, b) => b.exposure - a.exposure);

  const domainScores = domainScoresFrom(framework, input.maturity);

  const overall = aggregateOverall(exposures, ctx.overallPeakWeight);

  const recommendations = buildRecommendations(
    framework,
    input.maturity,
    exposures,
    ctx,
  );

  return {
    overall,
    overallLabel: ctx.severity(overall),
    topThreats: exposures.slice(0, 5),
    threatExposures: exposures,
    domainScores,
    recommendations,
    generatedAt: new Date().toISOString(),
  };
}

// Cell exposure for the consolidated heatmap (max or weighted-avg across use cases).
export function cellExposureMulti(
  framework: Framework,
  input: MultiAssessmentInput,
  threatId: string,
  domainId: string,
  mode: ConsolidationMode = "max",
  ctx: ScoringContext = DEFAULT_SCORING_CONTEXT,
): number | null {
  const cells = input.useCases
    .map((u) =>
      cellExposure(
        framework,
        {
          useCase: u.id,
          env: u.env,
          autonomyLevel: input.autonomyLevel,
          maturity: input.maturity,
        },
        threatId,
        domainId,
        ctx,
      ),
    )
    .filter((v): v is number => v !== null);
  if (cells.length === 0) return null;
  if (mode === "max") return Math.max(...cells);
  return cells.reduce((a, b) => a + b, 0) / input.useCases.length;
}
